/**
 * Compatibility client.
 * Keeps the `base44` object shape the pages already use, but talks to the InclusiveCore API.
 * Day-1 bridge — pages can migrate to the typed client (`@/api/client`) as they are touched.
 */
import { createAuthClient } from 'better-auth/react';
import { organizationClient } from 'better-auth/client/plugins';
import { ssoClient } from '@better-auth/sso/client';

// organizationClient/ssoClient aren't used by this ticket's pages yet — they're wired here so the
// auth client (and its generated types/hooks) are ready for the organisation switcher and SSO admin
// UI that later tickets add, without another churn pass through every page that imports authClient.
export const authClient = createAuthClient({
  baseURL: window.location.origin,
  basePath: '/auth',
  plugins: [organizationClient(), ssoClient()],
});

const API = '/api/v1';
// Active organisation (tenant) for every request — set by OrganisationContext when the user switches company.
let activeOrgId = null;
export function getActiveOrgId() { return activeOrgId; }
export function setActiveOrgId(id) { activeOrgId = id || null; try { if (id) localStorage.setItem('ic_active_org', id); else localStorage.removeItem('ic_active_org'); } catch {} }
export function clearActiveOrgId() { setActiveOrgId(null); }

class ApiError extends Error {
  constructor(status, data) { super(data?.error || data?.message || `HTTP ${status}`); this.status = status; this.data = data; this.code = data?.code; }
}

function shouldRefreshTenant(status, code) {
  return status === 403 || (status === 409 && ['org_suspended', 'org_archived'].includes(code));
}

async function http(method, path, body, opts = {}) {
  const res = await fetch(API + path, {
    method,
    credentials: 'include',
    headers: { ...(activeOrgId && !opts.skipTenant ? { 'X-Org-Id': activeOrgId } : {}), ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }), ...(opts.headers || {}) },
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    signal: opts.signal,
  });
  if (opts.raw) {
    if (!opts.skipTenant && (res.status === 403 || res.status === 409)) {
      const error = await res.clone().json().catch(() => null);
      if (shouldRefreshTenant(res.status, error?.code)) window.dispatchEvent(new CustomEvent('ic:tenant-denied', { detail: { status: res.status, code: error?.code } }));
    }
    return res;
  }
  const text = await res.text();
  let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    if (shouldRefreshTenant(res.status, data?.code) && !opts.skipTenant) window.dispatchEvent(new CustomEvent('ic:tenant-denied', { detail: { status: res.status, code: data?.code } }));
    throw new ApiError(res.status, data);
  }
  return data;
}

const qs = (o) => { const p = new URLSearchParams(); for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null) p.set(k, typeof v === 'object' ? JSON.stringify(v) : String(v)); const s = p.toString(); return s ? `?${s}` : ''; };

// Lightweight "realtime": poll + a cross-tab/local change bus so writes refresh views quickly.
const listeners = new Map(); // entity -> Set(cb)
function emit(entity) { for (const cb of listeners.get(entity) || []) { try { cb({ type: 'change', entity }); } catch {} } }
function entityApi(name) {
  const base = `/entities/${name}`;
  return {
    list: (sort, limit, opts) => http('GET', base + qs({ sort, limit }), undefined, opts),
    filter: (q, sort, limit) => http('GET', base + qs({ q, sort, limit })),
    get: (id) => http('GET', `${base}/${id}`),
    create: async (data) => { const r = await http('POST', base, data); emit(name); return r; },
    update: async (id, data, opts) => { const r = await http('PATCH', `${base}/${id}`, data, opts); emit(name); return r; },
    delete: async (id) => { const r = await http('DELETE', `${base}/${id}`); emit(name); return r; },
    bulkCreate: async (items) => { const r = await http('POST', `${base}/bulk`, { items }); emit(name); return r; },
    bulkUpdate: async (items) => { const r = await http('PATCH', `${base}/bulk`, { items }); emit(name); return r; },
    subscribe: (cb) => {
      if (!listeners.has(name)) listeners.set(name, new Set());
      listeners.get(name).add(cb);
      const t = setInterval(() => cb({ type: 'poll', entity: name }), 30000);
      return () => { clearInterval(t); listeners.get(name)?.delete(cb); };
    },
  };
}

const entities = new Proxy({}, { get: (_, name) => entityApi(String(name)) });

const auth = {
  // Bootstrap is deliberately tenant-free. The server is the authority for any
  // restored act-as target; localStorage is only a display hint.
  me: () => http('GET', '/me', undefined, { skipTenant: true }),
  isAuthenticated: async () => { try { await http('GET', '/me', undefined, { skipTenant: true }); return true; } catch { return false; } },
  logout: async (redirectUrl) => { try { await authClient.signOut(); } catch {} window.location.href = '/login' + (redirectUrl ? `?from_url=${encodeURIComponent(redirectUrl)}` : ''); },
  redirectToLogin: (fromUrl) => { window.location.href = '/login' + (fromUrl ? `?from_url=${encodeURIComponent(fromUrl)}` : ''); },
  setToken: () => {},
  loginViaEmailPassword: async (email, password) => {
    const r = await authClient.signIn.email({ email, password });
    if (r.error) throw new ApiError(r.error.status || 401, r.error);
    return r.data;
  },
  getSignInOptions: async (selector = {}, options = {}) => {
    const allowed = ['email', 'organisation_slug', 'invitation_id'];
    const keys = Object.keys(selector || {}).filter((key) => selector[key] !== undefined && selector[key] !== null && selector[key] !== '');
    if (keys.length > 1 || keys.some((key) => !allowed.includes(key))) {
      throw new ApiError(400, { code: 'invalid_sign_in_selector', error: 'Choose exactly one sign-in selector.' });
    }
    return http('POST', '/sign-in/options', keys.length ? { [keys[0]]: selector[keys[0]] } : {}, { skipTenant: true, signal: options.signal });
  },
  startSignIn: async (selector = {}, providerId, returnTo) => {
    const allowed = ['email', 'organisation_slug', 'invitation_id'];
    const keys = Object.keys(selector || {}).filter((key) => selector[key] !== undefined && selector[key] !== null && selector[key] !== '');
    if (keys.length > 1 || keys.some((key) => !allowed.includes(key)) || !providerId || !returnTo) {
      throw new ApiError(400, { code: 'invalid_sign_in_start', error: 'The sign-in request is incomplete.' });
    }
    return http('POST', '/sign-in/start', {
      ...(keys.length ? { [keys[0]]: selector[keys[0]] } : {}),
      provider_id: providerId,
      return_to: returnTo,
    }, { skipTenant: true });
  },
  // `opts` accepts either the original positional callbackURL string (kept working for any
  // caller still on the old signature) or an options object: { callbackURL, errorCallbackURL,
  // newUserCallbackURL, requestSignUp } — passed straight through to authClient.signIn.social.
  loginWithProvider: async (provider, opts) => {
    const p = provider === 'microsoft' ? 'microsoft' : 'google';
    const { callbackURL, errorCallbackURL, newUserCallbackURL, requestSignUp } =
      typeof opts === 'string' ? { callbackURL: opts } : opts || {};
    const r = await authClient.signIn.social({
      provider: p,
      callbackURL: callbackURL || window.location.origin,
      ...(errorCallbackURL ? { errorCallbackURL } : {}),
      ...(newUserCallbackURL ? { newUserCallbackURL } : {}),
      ...(requestSignUp !== undefined ? { requestSignUp } : {}),
    });
    if (r.error) throw new ApiError(r.error.status || 400, r.error);
    return r.data;
  },
  register: async ({ email, password, full_name, name, callbackURL }) => {
    const r = await authClient.signUp.email({
      email,
      password,
      name: full_name || name || email.split('@')[0],
      ...(callbackURL ? { callbackURL } : {}),
    });
    if (r.error) throw new ApiError(r.error.status || 400, r.error);
    return r.data;
  },
  resetPasswordRequest: async (email) => {
    const fn = authClient.requestPasswordReset || authClient.forgetPassword;
    const r = await fn({ email, redirectTo: `${window.location.origin}/reset-password` });
    if (r.error) throw new ApiError(r.error.status || 400, r.error);
    return r.data;
  },
  resetPassword: async (token, newPassword) => {
    const r = await authClient.resetPassword({ newPassword, token });
    if (r.error) throw new ApiError(r.error.status || 400, r.error);
    return r.data;
  },
  verifyOtp: async () => { throw new ApiError(400, { error: 'Email verification links are used instead of OTP codes.' }); },
  resendOtp: async (email, callbackURL) => {
    const r = await authClient.sendVerificationEmail({ email, callbackURL: callbackURL || window.location.origin });
    if (r?.error) throw new ApiError(r.error.status || 400, r.error);
    return r?.data;
  },
};

const actAs = {
  begin: (organisation_id, reason) => http('POST', '/admin/act-as', { organisation_id, reason }, { skipTenant: true }),
  stop: () => http('DELETE', '/admin/act-as', undefined, { skipTenant: true }),
};

// Legacy settings cards use this for response handling while sharing the
// confirmed in-memory tenant header and 403 recovery event.
const tenant = { request: (method, path, body, opts = {}) => http(method, path, body, { raw: true, ...opts }) };

const functions = {
  roadmapTaskSources: async (taskId, organisationId, options = {}) => {
    if (!organisationId) throw new ApiError(400, { error: 'Select an organisation to read the assessment source.' });
    return http('GET', `/functions/roadmapTaskSources/${encodeURIComponent(taskId)}`, undefined, { ...options, headers: { 'X-Org-Id': organisationId } });
  },
  assessmentEvidenceTasks: (assessmentId) => http('GET', `/functions/assessmentEvidenceTasks/${encodeURIComponent(assessmentId)}`),
  generateAssessmentEvidenceTasks: (assessmentId) => http('POST', '/functions/generateAssessmentEvidenceTasks', { assessment_id: assessmentId }),
  invoke: async (name, body = {}) => {
    const res = await http('POST', `/functions/${name}`, body, { raw: true });
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/pdf') || ct.includes('octet-stream')) return { status: res.status, data: await res.arrayBuffer() };
    const text = await res.text(); let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    if (!res.ok) throw new ApiError(res.status, data);
    return { status: res.status, data };
  },
};

// Version-aware assessment reads used by the assessment page.
const framework = {
  current: () => http('GET', '/framework/current'),
  questions: (id) => http('GET', `/framework/${encodeURIComponent(id)}/questions`),
  requirements: (id) => http('GET', `/framework/${encodeURIComponent(id)}/requirements`),
};

const integrations = {
  Core: {
    InvokeLLM: (params) => http('POST', '/ai/invoke', params),
    GenerateDocument: ({ kind, brief }) => http('POST', '/ai/generate', { kind, brief }),
    GenerateSpeech: async (params, options = {}) => {
      const audioEnabled = () => typeof document === 'undefined' || document.documentElement.getAttribute('data-app-audio') === 'on';
      if (!audioEnabled()) {
        return { url: null, cancelled: true, disabled: true };
      }
      const audioController = new AbortController();
      const stop = () => audioController.abort();
      window.addEventListener('inclusivecore:stop-audio', stop, { once: true });
      const signal = options?.signal ? AbortSignal.any([options.signal, audioController.signal]) : audioController.signal;
      let r;
      try {
        r = await http('POST', '/ai/speech', params, { signal });
      } catch {
        if (signal?.aborted) return { url: null, cancelled: true };
        r = { url: null };
      }
      if (signal.aborted || !audioEnabled()) return { url: null, cancelled: true, disabled: !audioEnabled() };
      if (r?.url) { window.removeEventListener('inclusivecore:stop-audio', stop); return r; }
      // Fallback: browser speech synthesis (no audio URL to return, so speak here and resolve when done).
      if (typeof window !== 'undefined' && window.speechSynthesis && params?.text) {
        await new Promise((resolve) => {
          const utterance = new SpeechSynthesisUtterance(params.text);
          const finish = () => {
            signal.removeEventListener('abort', cancel);
            window.removeEventListener('inclusivecore:stop-audio', stop);
            resolve();
          };
          const cancel = () => {
            window.speechSynthesis.cancel();
            finish();
          };
          utterance.lang = 'en-GB';
          utterance.rate = Number(document.documentElement.getAttribute('data-speech-rate')) || 1;
          const voiceId = document.documentElement.getAttribute('data-speech-voice');
          utterance.voice = window.speechSynthesis.getVoices().find((voice) => voice.voiceURI === voiceId) || null;
          utterance.onend = finish;
          utterance.onerror = finish;
          signal.addEventListener('abort', cancel, { once: true });
          if (signal.aborted || !audioEnabled()) return cancel();
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(utterance);
        });
      }
      window.removeEventListener('inclusivecore:stop-audio', stop);
      return { url: null, spoken: !signal.aborted, cancelled: signal.aborted };
    },
    UploadFile: async ({ file }) => { const fd = new FormData(); fd.append('file', file); return http('POST', '/files', fd); },
    UploadEvidence: async ({ file, task_ids = [], tags = [], suggested_tags = [], ...fields }, options = {}) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('task_ids', JSON.stringify(task_ids));
      fd.append('tags', JSON.stringify(tags));
      fd.append('suggested_tags', JSON.stringify(suggested_tags));
      for (const [key, value] of Object.entries(fields)) if (value !== undefined && value !== null && value !== '') fd.append(key, String(value));
      return http('POST', '/files/evidence', fd, { signal: options.signal });
    },
    UploadResource: async ({ file, ...fields }, options = {}) => {
      const fd = new FormData(); fd.append('file', file);
      for (const [key, value] of Object.entries(fields)) if (value !== undefined && value !== null && value !== '') fd.append(key, String(value));
      return http('POST', '/files/resources', fd, { signal: options.signal });
    },
    SendEmail: (params) => http('POST', '/functions/sendEmail', params),
  },
};

const agents = {
  createConversation: ({ agent_name, metadata }) => http('POST', '/agents/conversations', { agent_name, metadata }),
  getConversation: (id) => http('GET', `/agents/conversations/${id}`),
  addMessage: (conversation, message) => http('POST', `/agents/conversations/${conversation.id}/messages`, message),
  subscribeToConversation: (id, cb) => {
    const es = new EventSource(`${API}/agents/conversations/${id}/events${activeOrgId ? `?org=${encodeURIComponent(activeOrgId)}` : ''}`, { withCredentials: true });
    es.onmessage = (ev) => { try { cb(JSON.parse(ev.data)); } catch {} };
    return () => es.close();
  },
};

/**
 * Explicit IC-024 membership contract. Every operation carries its target in
 * the path and skips the ambient tenant header, so a provider/customer target
 * cannot be inferred from whichever organisation happens to be selected.
 */
const members = {
  list: (organisationId, query) => http('GET', `/orgs/${encodeURIComponent(organisationId)}/members${query ? qs(query) : ''}`, undefined, { skipTenant: true }),
  listInvitations: (organisationId, query) => http('GET', `/orgs/${encodeURIComponent(organisationId)}/invitations${query ? qs(query) : ''}`, undefined, { skipTenant: true }),
  invite: (organisationId, body) => http('POST', `/orgs/${encodeURIComponent(organisationId)}/invitations`, body, { skipTenant: true }),
  resendInvitation: (organisationId, invitationId) => http('POST', `/orgs/${encodeURIComponent(organisationId)}/invitations/${encodeURIComponent(invitationId)}/resend`, undefined, { skipTenant: true }),
  cancelInvitation: (organisationId, invitationId) => http('DELETE', `/orgs/${encodeURIComponent(organisationId)}/invitations/${encodeURIComponent(invitationId)}`, undefined, { skipTenant: true }),
  changeRole: (organisationId, userId, role) => http('PATCH', `/orgs/${encodeURIComponent(organisationId)}/members/${encodeURIComponent(userId)}/role`, { role }, { skipTenant: true }),
  remove: (organisationId, userId) => http('DELETE', `/orgs/${encodeURIComponent(organisationId)}/members/${encodeURIComponent(userId)}`, undefined, { skipTenant: true }),
  getAccessPolicy: (organisationId) => http('GET', `/orgs/${encodeURIComponent(organisationId)}/access-policy`, undefined, { skipTenant: true }),
  previewAccessPolicy: (organisationId, body) => http('POST', `/orgs/${encodeURIComponent(organisationId)}/access-policy/preview`, body, { skipTenant: true }),
  updateAccessPolicy: (organisationId, body) => http('PUT', `/orgs/${encodeURIComponent(organisationId)}/access-policy`, body, { skipTenant: true }),
  approveGuest: (organisationId, userId, body) => http('POST', `/orgs/${encodeURIComponent(organisationId)}/members/${encodeURIComponent(userId)}/guest`, body, { skipTenant: true }),
  revokeGuest: (organisationId, userId) => http('DELETE', `/orgs/${encodeURIComponent(organisationId)}/members/${encodeURIComponent(userId)}/guest`, undefined, { skipTenant: true }),
  revokeInvitationGuest: (organisationId, invitationId) => http('DELETE', `/orgs/${encodeURIComponent(organisationId)}/invitations/${encodeURIComponent(invitationId)}/guest`, undefined, { skipTenant: true }),
};

const personas = {
  list: (opts = {}) => http('GET', '/personas', undefined, opts),
  runFocusGroup: (body, opts = {}) => http('POST', '/personas/focus-groups', body, opts),
  avatarUrl: (slug) => `${API}/personas/${encodeURIComponent(slug)}/avatar`,
  adminList: (opts = {}) => http('GET', '/admin/personas', undefined, { ...opts, skipTenant: true }),
  adminGet: (id, opts = {}) => http('GET', `/admin/personas/${encodeURIComponent(id)}`, undefined, { ...opts, skipTenant: true }),
  adminAvatarUrl: (id) => `${API}/admin/personas/${encodeURIComponent(id)}/avatar`,
  adminCreate: (body) => http('POST', '/admin/personas', body, { skipTenant: true }),
  adminUpdate: (id, body) => http('PATCH', `/admin/personas/${encodeURIComponent(id)}`, body, { skipTenant: true }),
  adminRetire: (id, reason) => http('POST', `/admin/personas/${encodeURIComponent(id)}/retire`, { reason }, { skipTenant: true }),
  adminCorpus: (id, text) => http('POST', `/admin/personas/${encodeURIComponent(id)}/corpus`, { text }, { skipTenant: true }),
  adminPreview: (id, question) => http('POST', `/admin/personas/${encodeURIComponent(id)}/preview`, { question }, { skipTenant: true }),
  adminAvatar: (id, file) => { const form = new FormData(); form.append('file', file); return http('POST', `/admin/personas/${encodeURIComponent(id)}/avatar`, form, { skipTenant: true }); },
};

// Read bounded pages under one selected organisation. Never expose a partial collection.
const roadmapTasks = {
  async listAll(organisationId, { signal } = {}) {
    if (!organisationId) return [];
    const deadline = new AbortController();
    const timer = setTimeout(() => deadline.abort(new DOMException('Task loading timed out. Try again.', 'TimeoutError')), 60000);
    const combined = signal ? AbortSignal.any([signal, deadline.signal]) : deadline.signal;
    const rows = []; let after;
    try {
      do {
        combined.throwIfAborted();
        const page = await http('GET', '/entities/RoadmapTask' + qs({ paged: true, limit: 200, after }), undefined, { signal: combined, headers: { 'X-Org-Id': organisationId } });
        combined.throwIfAborted();
        if (!Array.isArray(page?.items) || page.items.length > 200 || !(page.next_cursor === null || typeof page.next_cursor === 'string')) throw new Error('Task list response is incomplete. Try again.');
        let previous = after || '';
        for (const row of page.items) {
          if (typeof row.id !== 'string' || row.id <= previous || row.organisation_id !== organisationId) throw new Error('Task list response is incomplete. Try again.');
          previous = row.id;
        }
        if (page.next_cursor !== null && (!page.items.length || page.next_cursor !== previous || page.next_cursor <= (after || ''))) throw new Error('Task list response is incomplete. Try again.');
        rows.push(...page.items); after = page.next_cursor;
      } while (after);
      return rows.sort((a, b) => (a.order - b.order) || a.id.localeCompare(b.id));
    } finally { clearTimeout(timer); }
  },
};

export const base44 = { entities, auth, actAs, tenant, functions, framework, integrations, agents, personas, members, roadmapTasks };
export default base44;
